import { useEffect, useRef } from "react";

/**
 * THE BURST — the app's finish (ExpertsApp ui/Burst.kt, v36 timings) for the web:
 * a white-hot flash, a pastel ring, ~700 sparks with streaking trails and a second wave,
 * additive light, Display P3 colour where the browser and screen have it.
 * Plays once each time `play` changes to a new number. Does nothing for visitors who ask
 * for reduced motion.
 */
const PALETTE = [
  [1.0, 0.60, 0.82], [1.0, 0.74, 0.52], [1.0, 0.94, 0.52], [0.55, 1.0, 0.78],
  [0.48, 0.92, 1.0], [0.64, 0.70, 1.0], [0.86, 0.64, 1.0], [1.0, 0.97, 0.88],
];
const SECONDS = 5.8;

function rng(seed) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

function makeSparks(n) {
  const r = rng(20261025);
  const out = [];
  for (let i = 0; i < n; i++) {
    const tier = r();
    const fast = tier < 0.32, core = tier > 0.8;
    out.push({
      a: r() * Math.PI * 2,
      v: fast ? 1.2 + r() : core ? 0.25 + r() * 0.35 : 0.6 + r() * 0.6,
      drag: 1.6 + r() * 1.6,
      life: 2.6 + r() * 1.8,
      delay: i % 4 === 0 ? 0.45 + r() * 0.9 : r() * 0.14,
      w: fast ? 1 + r() * 1.2 : 1.4 + r() * 2.2,
      c: i % PALETTE.length,
      tw: r() * Math.PI * 2,
    });
  }
  return out;
}

export default function Burst({ play, x = 0.5, y = 0.5, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!play) return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const canvas = ref.current;
    if (!canvas) return undefined;
    let ctx = null;
    try { ctx = canvas.getContext("2d", { colorSpace: "display-p3" }); } catch (_) { ctx = null; }
    if (!ctx) ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const p3 = (() => { try { return CSS.supports("color", "color(display-p3 1 1 1)"); } catch (_) { return false; } })();
    const col = (rgb, a) => (p3
      ? `color(display-p3 ${rgb[0]} ${rgb[1]} ${rgb[2]} / ${a})`
      : `rgba(${Math.round(rgb[0] * 255)}, ${Math.round(rgb[1] * 255)}, ${Math.round(rgb[2] * 255)}, ${a})`);
    const sparks = makeSparks(window.innerWidth < 720 ? 520 : 760);
    let raf = 0;
    const start = performance.now();
    const frame = (now) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = canvas.clientWidth, H = canvas.clientHeight;
      if (canvas.width !== Math.round(W * dpr)) { canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr); }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";
      // a frame's timestamp can be a hair earlier than the start mark — never let time run negative
      const t = Math.max(0, (now - start) / 1000);
      const ox = W * x, oy = H * y;
      const reach = Math.max(W, H) * 0.55;
      // flash
      const f = Math.min(1, t / 0.9);
      if (f < 1) {
        const rad = reach * (0.12 + 0.6 * f);
        const g = ctx.createRadialGradient(ox, oy, 0, ox, oy, rad);
        g.addColorStop(0, col(PALETTE[7], 1 - f));
        g.addColorStop(0.35, col(PALETTE[0], (1 - f) * 0.45));
        g.addColorStop(1, col(PALETTE[6], 0));
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(ox, oy, rad, 0, Math.PI * 2); ctx.fill();
      }
      // ring
      const rr = Math.min(1, t / 1.2);
      if (rr < 1) {
        const ease = 1 - Math.pow(1 - rr, 3);
        ctx.lineWidth = 8 * (1 - rr) + 1;
        ctx.strokeStyle = col(PALETTE[Math.floor(t * 10) % PALETTE.length], (1 - rr) * 0.85);
        ctx.beginPath(); ctx.arc(ox, oy, reach * (0.08 + 0.95 * ease), 0, Math.PI * 2); ctx.stroke();
      }
      // sparks
      ctx.lineCap = "round";
      const grav = 0.05 * reach;
      for (const s of sparks) {
        const lt = t - s.delay;
        if (lt <= 0) continue;
        const life = lt / s.life;
        if (life >= 1) continue;
        const v = s.v * reach, ca = Math.cos(s.a), sa = Math.sin(s.a);
        const px = (tt) => ox + (ca * v / s.drag) * (1 - Math.exp(-s.drag * tt));
        const py = (tt) => oy + (sa * v / s.drag) * (1 - Math.exp(-s.drag * tt)) + 0.5 * grav * tt * tt;
        const back = Math.max(0, lt - 0.05);
        const fade = life < 0.62 ? 1 : Math.pow((1 - life) / 0.38, 1.3);
        const a = fade * (0.62 + 0.38 * Math.sin(s.tw + lt * 26));
        const x1 = px(lt), y1 = py(lt);
        ctx.fillStyle = col(PALETTE[s.c], a * 0.16);
        ctx.beginPath(); ctx.arc(x1, y1, s.w * 3.2, 0, Math.PI * 2); ctx.fill();
        ctx.lineWidth = s.w;
        ctx.strokeStyle = col(PALETTE[s.c], a);
        ctx.beginPath(); ctx.moveTo(px(back), py(back)); ctx.lineTo(x1, y1); ctx.stroke();
      }
      if (t < SECONDS) raf = requestAnimationFrame(frame);
      else ctx.clearRect(0, 0, W, H);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [play, x, y]);
  return <canvas ref={ref} aria-hidden className={className} style={{ width: "100%", height: "100%", pointerEvents: "none" }} />;
}
